import React, { useEffect, useState } from 'react';
import { PageContent } from '@/shared/widgets/layout/PageContent';
import { Button } from '@/shared/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Badge } from '@/shared/ui/badge';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Save, RotateCcw, RefreshCw, AlertCircle, Plus } from 'lucide-react';
import { HighlightItemList } from '../components/HighlightItemList';
import { useHighlightManagement } from '../hooks/useHighlightManagement';
import { HIGHLIGHT_SECTIONS } from '../types/highlight.types';
import { ReactSelect, SelectOption } from '@/shared/widgets/react-select';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/shared/widgets/toast';
import { HighlightService } from '../services/highlightService';
import { FormFooter } from '@/shared/widgets/form-actions';
import { useTranslation } from '@/shared/i18n';

export default function HighlightManagementPage() {
  const { t } = useTranslation('highlights');
  const [selectedSection, setSelectedSection] = useState<string>('');
  const [selectedItemsToAdd, setSelectedItemsToAdd] = useState<string[]>([]);
  const [search, setSearch] = useState<string>('');
  const queryClient = useQueryClient();

  const {
    items,
    isLoading,
    isError,
    hasChanges,
    isSaving,
    isRemoving,
    reorderItems,
    saveChanges,
    resetChanges,
    removeItems,
    refetch,
    refreshItems,
  } = useHighlightManagement(selectedSection);

  // Query para buscar itens disponíveis
  const {
    data: availableItems,
    isLoading: isLoadingAvailable,
    refetch: refetchAvailableItems,
  } = useQuery({
    queryKey: ['available-items', selectedSection],
    queryFn: () => HighlightService.getAvailableItems(selectedSection, search),
    enabled: !!selectedSection,
  });

  // Mutation para adicionar itens
  const addItemsMutation = useMutation({
    mutationFn: (itemIds: string[]) => {
      const lastOrder = items.filter(item => item.order !== null).sort((a, b) => a.order - b.order)[items.length - 1].order;
      const newItemsWithOrder = itemIds.map(id => ({ id, order: lastOrder + 1 }));
      return HighlightService.addItemsToSection(selectedSection, newItemsWithOrder);
    }
    ,
    onSuccess: () => {
      toast.success(
        t('itemsAddedSuccess', { count: selectedItemsToAdd.length }),
        t('itemsAddedDescription', { sectionName: selectedSectionData?.name || '' })
      );
      setSelectedItemsToAdd([]);
      refreshItems();
      queryClient.invalidateQueries({
        queryKey: ['available-items', selectedSection],
      });
    },
    onError: (error: any) => {
      toast.api.error(
        t('errorAddingItems'),
        error.message || t('errorAddingItemsDescription')
      );
    },
  });

  const selectedSectionData = HIGHLIGHT_SECTIONS.find(
    (section) => section.key === selectedSection
  );

  // Seções em que só é possível reordenar os itens existentes (não adicionar/remover por aqui).
  const isOrderOnlySection = selectedSection === 'banners';

  const handleSectionChange = (value: string) => {
    setSelectedSection(value);
  };

  const handleSave = () => {
    saveChanges();
  };

  const handleReset = () => {
    resetChanges();
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleAddItems = () => {
    if (selectedItemsToAdd.length === 0) {
      toast.warning(t('noItemsSelected'), t('selectAtLeastOne'));
      return;
    }
    addItemsMutation.mutate(selectedItemsToAdd);
  };

  const handleRemoveItem = (itemId: string) => {
    removeItems([itemId]);
  };

  const availableOptions: SelectOption[] =
    availableItems?.map((item) => ({
      value: item.id,
      label: item.name,
      description: item.creatorName,
      icon: item.cover ? (
        <img src={item.cover} alt={item.name} className="w-6 h-6 rounded object-cover" />
      ) : undefined,
    })) || [];

  useEffect(() => {
    refetchAvailableItems();
  }, [search, refetchAvailableItems]);

  const getSectionName = (key: string) => {
    const sectionMap: Record<string, string> = {
      'trending-packs': t('trendingPacks'),
      'top-creators': t('topCreators'),
      'top-photographs': t('topPhotographs'),
      'popular-packs-paid': t('popularPacksPaid'),
      'popular-presets-free': t('popularPresetsFree'),
      'banners': t('banners'),
    };
    return sectionMap[key] || key;
  };

  return (
    <PageContent
      title={t('manageSections')}
      items={[
        {
          label: t('home'),
          href: "/",
        },
        {
          label: t('manageSectionsBreadcrumb'),
          href: "/admin/highlights",
        },
      ]}
    >
      <div className="space-y-6">
        {/* Seletor de Seção */}
        <Card className="bg-[#222222] border-white/10">
          <CardHeader>
            <CardTitle className="text-white">{t('selectSection')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <Select value={selectedSection} onValueChange={handleSectionChange}>
                <SelectTrigger className="w-[300px] bg-[#333333] border-white/10 text-white">
                  <SelectValue placeholder={t('chooseSection')} />
                </SelectTrigger>
                <SelectContent className="bg-[#333333] border-white/10">
                  {HIGHLIGHT_SECTIONS.map((section) => (
                    <SelectItem key={section.key} value={section.key} className="text-white hover:bg-white/10">
                      {getSectionName(section.key)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {selectedSectionData && (
                <Badge variant="secondary" className="bg-[#D91657] text-white">
                  {getSectionName(selectedSectionData.key)}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Ações */}
        {selectedSection && (
          <Card className="bg-[#222222] border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center justify-between text-white">
                <span>{t('manageOrder')}</span>
                <div className="flex items-center gap-2">
                  {hasChanges && (
                    <Badge variant="outline" className="text-orange-400 border-orange-400">
                      {t('unsavedChanges')}
                    </Badge>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefresh}
                    disabled={isLoading || isSaving}
                    className="bg-[#333333] border-white/10 text-white hover:bg-white/10"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    {t('refresh')}
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">

                {/* Seletor de itens para adicionar */}
                {!isOrderOnlySection && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-300">
                      {t('addNewItems')}
                    </label>
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <ReactSelect
                          options={availableOptions}
                          value={selectedItemsToAdd}
                          onChange={(value) => setSelectedItemsToAdd(Array.isArray(value) ? value : [])}
                          placeholder={t('selectItemsToAdd')}
                          multiple={true}
                          searchable={true}
                          clearable={true}
                          loading={isLoadingAvailable}
                          noOptionsMessage={t('noItemsAvailable')}
                          className="min-w-[400px]"
                          onSearch={setSearch}
                          filterOption={() => true}
                        />
                      </div>
                      <Button
                        onClick={handleAddItems}
                        disabled={selectedItemsToAdd.length === 0 || addItemsMutation.isPending}
                        className="bg-[#D91657] hover:bg-[#C1174F] text-white"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        {addItemsMutation.isPending ? t('adding') : `${t('add')} ${selectedItemsToAdd.length}`}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Instruções */}
                <Alert className="bg-[#333333] border-white/10">
                  <AlertCircle className="h-4 w-4 text-[#D91657]" />
                  <AlertDescription className="text-white/70">
                    {t('dragDropInstructions')}
                  </AlertDescription>
                </Alert>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Lista de Itens */}
        {selectedSection && (
          <Card className="bg-[#222222] border-white/10">
            <CardHeader>
              <CardTitle className="text-white">
                {t('sectionItems')} {getSectionName(selectedSectionData?.key || '')}
                {items.length > 0 && (
                  <Badge variant="secondary" className="ml-2 bg-[#D91657] text-white">
                    {items.length} {items.length !== 1 ? t('itemsPlural') : t('items')}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isError ? (
                <Alert variant="destructive" className="bg-red-900/20 border-red-500/20">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-red-200">
                    {t('errorLoadingSectionItems')}
                  </AlertDescription>
                </Alert>
              ) : (
                <HighlightItemList
                  items={items}
                  onReorder={reorderItems}
                  onRemove={handleRemoveItem}
                  allowRemove={!isOrderOnlySection}
                  isLoading={isLoading}
                  isRemoving={isRemoving}
                />
              )}
            </CardContent>
          </Card>
        )}

        {/* Mensagem quando nenhuma seção está selecionada */}
        {!selectedSection && (
          <Card className="bg-[#222222] border-white/10">
            <CardContent className="text-center py-12">
              <div className="text-white/40 mb-4">
                <AlertCircle className="h-12 w-12 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">
                {t('selectSection')}
              </h3>
              <p className="text-white/60">
                {t('selectSectionDescription')}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Footer com botões de ação */}
        {selectedSection && (
          <FormFooter className="mt-8">
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleReset}
                disabled={!hasChanges || isSaving}
                className="bg-transparent border-[#333333] text-white hover:bg-[#222222] hover:text-white/60"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                {t('discardChanges')}
              </Button>
              <Button
                onClick={handleSave}
                disabled={!hasChanges || isSaving}
                className="bg-[#D91657] hover:bg-[#C1174F] text-white"
              >
                <Save className="h-4 w-4 mr-2" />
                {isSaving ? t('saving') : t('saveChanges')}
              </Button>
            </div>
          </FormFooter>
        )}
      </div>
    </PageContent>
  );
}
